import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import Typo from 'typo-js';
import { SpellcheckMatch } from '../models/sh-editor-spellcheck.model';

@Injectable({
    providedIn: 'root'
})
export class ShEditorSpellcheckService {
    private static readonly MAX_SUGGESTIONS = 4;
    private static readonly TOKENS_PER_BATCH = 10;

    private readonly translateService = inject(TranslateService);

    private readonly dictDe = new Typo('de_DE', null, null, { dictionaryPath: '/assets/dictionaries' });
    private readonly dictEn = new Typo('en_US', null, null, { dictionaryPath: '/assets/dictionaries' });

    private readonly htmlEntitiesPattern = /&[a-zA-Z]+;/g;
    private readonly htmlTagsPattern = /<[^>]*>/g;
    private readonly nonWordPattern = /^[\W_]+|[\W_]+$/g;

    private readonly entityMap: Record<string, string> = {
        '&nbsp;': ' ',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&apos;': "'"
    };

    public async findMisspellings(doc: ProseMirrorNode, signal?: AbortSignal): Promise<SpellcheckMatch[]> {
        const misspellings: SpellcheckMatch[] = [];
        let processedTokens = 0;

        for (const { text, pos } of this.collectSpellcheckSegments(doc)) {
            if (signal?.aborted) {
                return [];
            }

            const tokenPattern = /\S+/g;
            let token = tokenPattern.exec(text);
            while (token) {
                const word = token[0];
                const start = token.index;

                if (word.trim() && !this.checkSpelling(word)) {
                    misspellings.push({
                        from: pos + start,
                        to: pos + start + word.length,
                        word
                    });
                }

                processedTokens += 1;
                if (processedTokens % ShEditorSpellcheckService.TOKENS_PER_BATCH === 0) {
                    await this.yieldToMainThread(signal);
                    if (signal?.aborted) {
                        return [];
                    }
                }

                token = tokenPattern.exec(text);
            }
        }

        return misspellings;
    }

    public async getSuggestions(word: string, signal?: AbortSignal): Promise<string[]> {
        await this.yieldToMainThread(signal);
        if (signal?.aborted) {
            return [];
        }

        return this.buildSuggestions(word);
    }

    private collectSpellcheckSegments(doc: ProseMirrorNode): Array<{ text: string; pos: number }> {
        const segments: Array<{ text: string; pos: number }> = [];

        doc.descendants((node, pos) => {
            if (!node.isText || !node.text || node.marks.some((mark) => mark.type.name === 'link')) {
                return true;
            }

            segments.push({
                text: node.text,
                pos
            });

            return true;
        });

        return segments;
    }

    private async yieldToMainThread(signal?: AbortSignal): Promise<void> {
        if (signal?.aborted) {
            return;
        }

        await new Promise<void>((resolve) => {
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => resolve());
                return;
            }

            setTimeout(resolve, 0);
        });
    }

    private cleanWord(word: string): string {
        return word
            .replace(this.htmlEntitiesPattern, (match) => this.entityMap[match] || '')
            .replace(this.htmlTagsPattern, '')
            .replace(this.nonWordPattern, '')
            .replace(/\u200B/g, '')
            .trim();
    }

    private getDictionary(lang: string): Typo {
        const normalized = lang.toLowerCase().split(/[-_]/)[0];

        switch (normalized) {
            case 'de':
                return this.dictDe;
            case 'en':
                return this.dictEn;
            default:
                throw new Error(`Unsupported spellcheck language: ${lang}`);
        }
    }

    private getActiveDictionary(): Typo {
        return this.getDictionary(this.translateService.currentLang || this.translateService.defaultLang || 'en');
    }

    private normalizeWordForDictionary(word: string): string {
        if (!/\d/.test(word)) {
            return this.cleanWord(word);
        }

        const substitutions = new Map([
            ['1', 'i'],
            ['3', 'e'],
            ['4', 'a'],
            ['5', 's'],
            ['0', 'o'],
            ['7', 't']
        ]);

        let normalizedWord = word;
        for (const [number, letter] of substitutions) {
            normalizedWord = normalizedWord.replace(new RegExp(number, 'g'), letter);
        }

        return this.cleanWord(normalizedWord);
    }

    private checkSpelling(word: string): boolean {
        if (!/\d/.test(word)) {
            return this.performRegularSpellCheck(word);
        }

        const cleanedWord = this.normalizeWordForDictionary(word);
        const dictionary = this.getActiveDictionary();

        if (!cleanedWord) {
            return true;
        }

        if (dictionary.check(cleanedWord)) {
            return false;
        }

        return true;
    }

    private buildSuggestions(word: string): string[] {
        const cleanedWord = this.normalizeWordForDictionary(word);
        if (!cleanedWord) {
            return [];
        }

        const dictionary = this.getActiveDictionary();
        if (/\d/.test(word) && dictionary.check(cleanedWord)) {
            return [cleanedWord];
        }

        const rawSuggestions = dictionary.suggest(cleanedWord, ShEditorSpellcheckService.MAX_SUGGESTIONS * 3);

        return this.filterSuggestions(cleanedWord, rawSuggestions);
    }

    private filterSuggestions(word: string, suggestions: string[]): string[] {
        const normalizedWord = word.toLowerCase();
        const maxDistance =
            normalizedWord.length <= 4 ? 1 : normalizedWord.length <= 7 ? 2 : Math.max(3, Math.floor(word.length / 3));

        return suggestions
            .map((suggestion) => this.cleanWord(suggestion))
            .filter(Boolean)
            .filter(
                (suggestion, index, allSuggestions) =>
                    allSuggestions.findIndex((candidate) => candidate.toLowerCase() === suggestion.toLowerCase()) ===
                    index
            )
            .filter((suggestion) => {
                const normalizedSuggestion = suggestion.toLowerCase();
                const distance = this.getLevenshteinDistance(normalizedWord, normalizedSuggestion);
                const sameFirstLetter = normalizedWord.charAt(0) === normalizedSuggestion.charAt(0);
                const sameLastLetter =
                    normalizedWord.charAt(normalizedWord.length - 1) ===
                    normalizedSuggestion.charAt(normalizedSuggestion.length - 1);

                return distance <= maxDistance && (sameFirstLetter || sameLastLetter || distance <= 1);
            })
            .slice(0, ShEditorSpellcheckService.MAX_SUGGESTIONS);
    }

    private getLevenshteinDistance(source: string, target: string): number {
        const distanceMatrix = Array.from({ length: source.length + 1 }, () => new Array(target.length + 1).fill(0));

        for (let sourceIndex = 0; sourceIndex <= source.length; sourceIndex += 1) {
            distanceMatrix[sourceIndex][0] = sourceIndex;
        }

        for (let targetIndex = 0; targetIndex <= target.length; targetIndex += 1) {
            distanceMatrix[0][targetIndex] = targetIndex;
        }

        for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
            for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
                const substitutionCost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;

                distanceMatrix[sourceIndex][targetIndex] = Math.min(
                    distanceMatrix[sourceIndex - 1][targetIndex] + 1,
                    distanceMatrix[sourceIndex][targetIndex - 1] + 1,
                    distanceMatrix[sourceIndex - 1][targetIndex - 1] + substitutionCost
                );
            }
        }

        return distanceMatrix[source.length][target.length];
    }

    private performRegularSpellCheck(word: string): boolean {
        const cleanedWord = this.cleanWord(word);
        if (!cleanedWord) {
            return true;
        }

        const dictionary = this.getActiveDictionary();

        return dictionary.check(cleanedWord);
    }
}
